"use client";

/**
 * User Context Provider
 *
 * When signed in with Supabase (e.g. Google), user.id is the auth user id so profiles
 * follow the account across devices. When signed out, uses local identity (device-only).
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { getUserIdentity, setCachedAuthUserId, type UserIdentity } from "./identity";
import { createClient } from "@/lib/supabase/client";
import {
  hydrateFromSupabase,
  migrateLocalAnonymousProfilesToAuthUser,
} from "@/lib/supabase/syncProfiles";
import {
  hydrateMessagesFromSupabase,
  migrateLocalMessagesToAuthUser,
} from "@/lib/supabase/syncMessages";

interface UserContextType {
  user: UserIdentity | null;
  loading: boolean;
  refresh: () => void;
}

const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  refresh: () => {},
});

export function useUser() {
  return useContext(UserContext);
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserIdentity | null>(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback(async (session: Session | null) => {
    try {
      if (session?.user) {
        const authUser = session.user;
        setCachedAuthUserId(authUser.id);
        await migrateLocalAnonymousProfilesToAuthUser(authUser.id);
        await hydrateFromSupabase(authUser.id);
        // Profiles have to land locally (with the correct ownerId) before
        // this runs — migrateLocalMessagesToAuthUser cross-references
        // aiProfiles.ownerId to figure out which local messages are this
        // user's, since ChatMessage itself carries no owner field.
        await migrateLocalMessagesToAuthUser(authUser.id);
        await hydrateMessagesFromSupabase(authUser.id);
        setUser({
          id: authUser.id,
          createdAt: authUser.created_at ? new Date(authUser.created_at).getTime() : Date.now(),
          email: authUser.email ?? undefined,
          isSupabaseUser: true,
        });
      } else {
        setCachedAuthUserId(null);
        setUser(getUserIdentity());
      }
    } catch (error) {
      console.error("Failed to apply auth session:", error);
      setCachedAuthUserId(null);
      setUser(getUserIdentity());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      setLoading(false);
      return;
    }

    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void applySession(session);
    });

    return () => subscription.unsubscribe();
  }, [applySession]);

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    setLoading(true);
    const supabase = createClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    await applySession(session);
  }, [applySession]);

  return (
    <UserContext.Provider value={{ user, loading, refresh }}>
      {children}
    </UserContext.Provider>
  );
}

